import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Phone, Mail, Globe, Youtube, Linkedin, Instagram, Facebook } from 'lucide-react';
import { SERVICES } from '../data/services';
import { CYBER_GUY_CHANNEL_URL } from '../data/videos';

// TikTok isn't in lucide-react; use inline SVG.
const TikTokIcon = ({ className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.3 0 .58.05.85.13V9.4a6.34 6.34 0 0 0-1-.08A6.33 6.33 0 1 0 15.63 18.6V9.34a8.16 8.16 0 0 0 4.77 1.52V7.42a4.85 4.85 0 0 1-.81-.73z" />
  </svg>
);

const socialLinks = [
  { href: CYBER_GUY_CHANNEL_URL, icon: Youtube, label: 'YouTube' },
  { href: '#', icon: Linkedin, label: 'LinkedIn' },
  { href: 'https://www.instagram.com/atikukeke', icon: Instagram, label: 'Instagram' },
  { href: '#', icon: TikTokIcon, label: 'TikTok' },
  { href: '#', icon: Facebook, label: 'Facebook' },
];

const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-slate-950 border-t border-cyan-500/15 mt-16" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid gap-10 md:grid-cols-4">
          {/* Brand */}
          <div className="md:col-span-2 md:pr-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-gradient-to-br from-cyan-400/20 to-blue-500/20 rounded-lg border border-cyan-400/30">
                <Shield className="w-7 h-7 text-cyan-300" />
              </div>
              <div>
                <div className="text-lg font-bold text-white tracking-tight">KENNYVICOPS Cyber Solutions Inc.</div>
                <div className="text-xs text-cyan-300/90 uppercase tracking-widest">Practical Cybersecurity Solutions</div>
              </div>
            </div>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-5 max-w-md">
              <span className="text-white font-semibold">Protect your business. Reduce your risk. Build resilience.</span>
              <br />
              Practical cybersecurity for small and growing businesses.
            </p>

            <div className="mt-6">
              <p className="text-xs uppercase tracking-widest text-cyan-300/80 mb-2">Follow The Cyber Guy</p>
              <p className="text-xs text-slate-400 mb-3">Simple. Practical. Actionable Cybersecurity.</p>
              <div className="flex flex-wrap gap-2">
                {socialLinks.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target={s.href.startsWith('http') ? '_blank' : undefined}
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    data-testid={`social-${s.label.toLowerCase()}`}
                    className="p-2.5 bg-slate-900/70 hover:bg-cyan-500/15 border border-cyan-500/20 hover:border-cyan-400/50 rounded-lg transition-colors"
                  >
                    <s.icon className="w-4 h-4 text-slate-300 hover:text-cyan-300" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4 uppercase tracking-widest">Navigate</h4>
            <ul className="space-y-2.5 text-sm">
              {[
                { path: '/', label: 'Home' },
                { path: '/about', label: 'About' },
                { path: '/services', label: 'Services' },
                { path: '/insights', label: 'Insights' },
                { path: '/the-cyber-guy', label: 'The Cyber Guy' },
                { path: '/contact', label: 'Contact' },
              ].map((n) => (
                <li key={n.path}>
                  <Link to={n.path} className="text-slate-300 hover:text-cyan-300 transition-colors">
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>

            <h4 className="text-white font-semibold text-sm mt-8 mb-4 uppercase tracking-widest">Free Tools</h4>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link
                  to="/security-health-check"
                  data-testid="footer-healthcheck"
                  className="text-cyan-300 hover:text-cyan-200 transition-colors font-medium"
                >
                  Security Health Check
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4 uppercase tracking-widest">Services</h4>
            <ul className="space-y-2.5 text-sm">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="text-slate-300 hover:text-cyan-300 transition-colors"
                    data-testid={`footer-service-${s.slug}`}
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact strip */}
        <div className="mt-10 pt-8 border-t border-cyan-500/15 grid gap-5 sm:grid-cols-3 text-sm">
          <a href="tel:+14379334970" className="flex items-center gap-3 text-slate-300 hover:text-cyan-300">
            <Phone className="w-4 h-4 text-cyan-300 flex-shrink-0" />
            +1 (437) 933-4970
          </a>
          <a href="mailto:kenny@kennyvicops.com" className="flex items-center gap-3 text-slate-300 hover:text-cyan-300 break-all">
            <Mail className="w-4 h-4 text-cyan-300 flex-shrink-0" />
            kenny@kennyvicops.com
          </a>
          <a href="https://kennyvicops.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-slate-300 hover:text-cyan-300">
            <Globe className="w-4 h-4 text-cyan-300 flex-shrink-0" />
            kennyvicops.com
          </a>
        </div>
      </div>

      <div className="border-t border-cyan-500/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-slate-500">
          <p>© {year} KENNYVICOPS Cyber Solutions Inc. All rights reserved.</p>
          <div className="flex gap-5">
            <Link to="/privacy" className="hover:text-cyan-300">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-cyan-300">Terms of Use</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
